// src/components/Review/ReviewModal.js
import React, { useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stack,
  Avatar,
  ToggleButtonGroup,
  ToggleButton,
  TextField,
  Chip,
  IconButton,
  LinearProgress,
  Collapse,
  useTheme,
  useMediaQuery,
  Fade,
  Slide,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import StarHalfIcon from '@mui/icons-material/StarHalf';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import PaletteIcon from '@mui/icons-material/Palette';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import VerifiedIcon from '@mui/icons-material/Verified';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ChatIcon from '@mui/icons-material/Chat';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';

const METRICS = [
  {
    key: 'contentQuality',
    title: 'Content Quality',
    icon: '🎨',
    IconComponent: PaletteIcon,
    helper: 'How satisfied are you with the overall content?',
  },
  {
    key: 'contentFrequency',
    title: 'Content Frequency',
    icon: '📅',
    IconComponent: CalendarTodayIcon,
    helper: 'How often does this creator post new content?',
  },
  {
    key: 'authenticity',
    title: 'Authenticity',
    icon: '✨',
    IconComponent: VerifiedIcon,
    helper: 'Does the creator deliver what they advertise?',
  },
  {
    key: 'consistency',
    title: 'Consistency',
    icon: '📊',
    IconComponent: TrendingUpIcon,
    helper: 'How reliable and consistent is their content delivery?',
  },
  {
    key: 'engagement',
    title: 'Engagement',
    icon: '💬',
    IconComponent: ChatIcon,
    helper: 'How well do they interact with subscribers?',
  },
];

const SPEND_OPTIONS = [
  { value: 'prefer-not', label: 'Prefer not to say', icon: '🤐' },
  { value: '0-25', label: '$0–25', icon: '💵' },
  { value: '25-50', label: '$25–50', icon: '💵💵' },
  { value: '50-100', label: '$50–100', icon: '💰' },
  { value: '100-250', label: '$100–250', icon: '💰💰' },
  { value: '250+', label: '$250+', icon: '💎' },
];

// Star Rating Component
const StarRating = ({ value, onChange, size = 'large' }) => {
  const theme = useTheme();
  const [hoverValue, setHoverValue] = useState(0);
  const displayValue = hoverValue || value;
  
  const starSize = size === 'large' ? 40 : 32;

  const handleStarClick = (star) => {
    // Immediately update without relying on hover state
    onChange(star);
  };

  return (
    <Stack direction="row" spacing={0.5} justifyContent="center">
      {[1, 2, 3, 4, 5].map((star) => (
        <IconButton
          key={star}
          onClick={() => handleStarClick(star)}
          onTouchEnd={(e) => {
            e.preventDefault();
            handleStarClick(star);
          }}
          onMouseEnter={() => setHoverValue(star)}
          onMouseLeave={() => setHoverValue(0)}
          sx={{
            p: 0.5,
            transition: 'all 0.2s ease-in-out',
            transform: displayValue >= star ? 'scale(1.05)' : 'scale(1)',
            '&:hover': {
              transform: 'scale(1.15)',
            },
            '&:active': {
              transform: 'scale(0.95)',
            },
          }}
        >
          {displayValue >= star ? (
            <StarIcon
              sx={{
                fontSize: starSize,
                color: theme.palette.warning.main,
                filter: 'drop-shadow(0 2px 4px rgba(255, 152, 0, 0.3))',
                transition: 'all 0.2s ease-in-out',
              }}
            />
          ) : (
            <StarBorderIcon
              sx={{
                fontSize: starSize,
                color: alpha(theme.palette.text.secondary, 0.3),
                transition: 'all 0.2s ease-in-out',
              }}
            />
          )}
        </IconButton>
      ))}
    </Stack>
  );
};

export default function ReviewModal({
  open,
  onClose,
  reviewCreator,
  reviewForm,
  setReviewForm,
  onSubmit,
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [currentStep, setCurrentStep] = useState(0);
  const [slideDirection, setSlideDirection] = useState('left');

  // Total steps: Welcome(0) + Recommend(1) + 5 Ratings(2-6) + Investment(7) + Written(8) = 9 total
  // But we only show steps 1-7 in the counter (excluding welcome and written review)
  const totalSteps = 9;
  const totalCountedSteps = 7; // Only count actual required steps
  const progressPercent = ((currentStep) / totalSteps) * 100;
  
  // Get display step number (0 for welcome, 1-7 for actual steps, 0 for final)
  const getDisplayStep = () => {
    if (currentStep === 0 || currentStep === 8) return null;
    return currentStep; // Steps 1-7
  };

  const handleNext = () => {
    setSlideDirection('left');
    setCurrentStep((prev) => Math.min(prev + 1, totalSteps));
  };

  const handleBack = () => {
    setSlideDirection('right');
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  const handleClose = () => {
    // Reset step and clear form data
    setCurrentStep(0);
    setReviewForm({
      recommend: '',
      spent: '',
      contentQuality: 0,
      contentFrequency: 0,
      authenticity: 0,
      consistency: 0,
      engagement: 0,
      fullReview: '',
    });
    onClose();
  };

  const canProgress = useMemo(() => {
    switch (currentStep) {
      case 0: return true; // Welcome
      case 1: return !!reviewForm.recommend; // Recommendation
      case 2: return Number(reviewForm.contentQuality) > 0;
      case 3: return Number(reviewForm.contentFrequency) > 0;
      case 4: return Number(reviewForm.authenticity) > 0;
      case 5: return Number(reviewForm.consistency) > 0;
      case 6: return Number(reviewForm.engagement) > 0;
      case 7: return !!reviewForm.spent; // Investment
      case 8: return true; // Written review (optional)
      default: return false;
    }
  }, [currentStep, reviewForm]);

  const isSubmitDisabled = useMemo(() => {
    const ratingsCompleted = METRICS.every(({ key }) => Number(reviewForm[key]) > 0);
    return !ratingsCompleted || !reviewForm.recommend || !reviewForm.spent;
  }, [reviewForm]);

  // Step content renderer
  const renderStepContent = () => {
    const metric = METRICS[currentStep - 2]; // For rating steps 2-6

    switch (currentStep) {
      case 0: // Welcome
        return (
          <Box sx={{ textAlign: 'center', py: { xs: 4, md: 6 } }}>
            <Avatar
              src={reviewCreator?.avatar || reviewCreator?.profilePicture || '/default-profile.png'}
              alt={reviewCreator?.creatorName || reviewCreator?.username || 'Creator'}
              sx={{
                width: { xs: 100, md: 120 },
                height: { xs: 100, md: 120 },
                mx: 'auto',
                mb: 3,
                border: `4px solid ${theme.palette.primary.main}`,
                boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
              }}
            />
            <Typography variant="h3" fontWeight={800} gutterBottom sx={{ fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' } }}>
              Review {reviewCreator?.creatorName || reviewCreator?.username}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ mb: 3 }}>
              <AccessTimeIcon sx={{ color: theme.palette.primary.main }} />
              <Typography variant="h6" color="primary" fontWeight={600}>
                Takes less than 1 minute
              </Typography>
            </Stack>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4, fontSize: { xs: '1rem', sm: '1.1rem' }, maxWidth: 500, mx: 'auto' }}>
              Your honest feedback helps others save money and make informed decisions about who to support.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Chip icon={<SaveIcon />} label="Save others money" color="primary" sx={{ fontWeight: 600, fontSize: '0.9rem', py: 2.5 }} />
            </Box>
          </Box>
        );

      case 1: // Recommendation
        return (
          <Box sx={{ py: { xs: 2, md: 2 } }}>
            <Typography variant="h4" fontWeight={700} gutterBottom textAlign="center" sx={{ mb: 1, fontSize: { xs: '1.75rem', sm: '2rem', md: '2.25rem' } }}>
              Would you recommend this creator?
            </Typography>
            <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ mb: 3, fontSize: { xs: '1.1rem', md: '1.2rem' } }}>
              Be honest — your opinion matters!
            </Typography>
            <ToggleButtonGroup
              value={reviewForm.recommend}
              exclusive
              onChange={(e, val) => val && setReviewForm((f) => ({ ...f, recommend: val }))}
              fullWidth
              sx={{
                gap: 3,
                '& .MuiToggleButtonGroup-grouped': {
                  flex: 1,
                  border: `3px solid ${alpha(theme.palette.divider, 0.2)}`,
                  borderRadius: '16px !important',
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: { xs: '1.1rem', sm: '1.3rem' },
                  py: { xs: 3, sm: 4 },
                  transition: 'all 0.3s ease-in-out',
                  backgroundColor: theme.palette.background.paper,
                  '&:hover': {
                    transform: 'scale(1.02)',
                    boxShadow: '0 12px 24px rgba(0,0,0,0.1)',
                  },
                },
                '& .MuiToggleButtonGroup-grouped[value="yes"]': {
                  '&.Mui-selected': {
                    background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                    color: theme.palette.common.white,
                    borderColor: theme.palette.success.main,
                    boxShadow: '0 12px 32px rgba(76,175,80,0.4)',
                    transform: 'scale(1.02)',
                  },
                },
                '& .MuiToggleButtonGroup-grouped[value="no"]': {
                  '&.Mui-selected': {
                    background: `linear-gradient(135deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`,
                    color: theme.palette.common.white,
                    borderColor: theme.palette.error.main,
                    boxShadow: '0 12px 32px rgba(244,67,54,0.4)',
                    transform: 'scale(1.02)',
                  },
                },
              }}
            >
              <ToggleButton value="yes">
                <ThumbUpIcon sx={{ mr: 1.5, fontSize: 32 }} /> Absolutely!
              </ToggleButton>
              <ToggleButton value="no">
                <ThumbDownIcon sx={{ mr: 1.5, fontSize: 32 }} /> Not Really
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        );

      case 2:
      case 3:
      case 4:
      case 5:
      case 6: // Rating steps
        if (!metric) return null;
        const currentRating = Number(reviewForm[metric.key]);
        return (
          <Box sx={{ py: { xs: 2, md: 2 }, textAlign: 'center' }}>
            <Box
              sx={{
                width: { xs: 80, md: 96 },
                height: { xs: 80, md: 96 },
                borderRadius: 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                mx: 'auto',
                mb: 2.5,
              }}
            >
              <metric.IconComponent sx={{ fontSize: { xs: 48, md: 64 }, color: theme.palette.primary.main }} />
            </Box>
            <Typography variant="h4" fontWeight={700} gutterBottom sx={{ fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' } }}>
              {metric.title}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3, fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
              {metric.helper}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <StarRating
                value={currentRating}
                onChange={(val) => {
                  setReviewForm((f) => ({ ...f, [metric.key]: val }));
                }}
                size="large"
              />
            </Box>
            {currentRating > 0 && (
              <Chip
                label={`${currentRating} out of 5 stars`}
                sx={{
                  backgroundColor: theme.palette.warning.main,
                  color: theme.palette.common.white,
                  fontWeight: 700,
                  fontSize: { xs: '1rem', md: '1.1rem' },
                  py: { xs: 2.5, md: 3 },
                  px: { xs: 1, md: 2 },
                }}
              />
            )}
          </Box>
        );

      case 7: // Investment
        return (
          <Box sx={{ py: { xs: 2, md: 2 } }}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <AttachMoneyIcon sx={{ fontSize: { xs: 64, md: 80 }, color: theme.palette.success.main, mb: 2 }} />
              <Typography variant="h4" fontWeight={700} gutterBottom sx={{ fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' } }}>
                Investment Level
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
                Help others understand the value
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                gap: 2,
              }}
            >
              {SPEND_OPTIONS.map((opt) => (
                <Button
                  key={opt.value}
                  variant={reviewForm.spent === opt.value ? 'contained' : 'outlined'}
                  onClick={() => setReviewForm((f) => ({ ...f, spent: opt.value }))}
                  sx={{
                    py: 3,
                    borderRadius: 3,
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: '1.1rem',
                    border: `3px solid ${reviewForm.spent === opt.value ? 'transparent' : alpha(theme.palette.divider, 0.2)}`,
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'scale(1.02)',
                      boxShadow: '0 8px 20px rgba(0,0,0,0.1)',
                    },
                    ...(reviewForm.spent === opt.value && {
                      background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                      boxShadow: '0 8px 24px rgba(76,175,80,0.4)',
                    }),
                  }}
                >
                  <span style={{ marginRight: '12px', fontSize: '1.5rem' }}>{opt.icon}</span>
                  {opt.label}
                </Button>
              ))}
            </Box>
          </Box>
        );

      case 8: // Written review
        return (
          <Box sx={{ py: { xs: 2, md: 2 } }}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <ChatIcon sx={{ fontSize: { xs: 64, md: 80 }, color: theme.palette.primary.main, mb: 2 }} />
              <Typography variant="h4" fontWeight={700} gutterBottom sx={{ fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' } }}>
                Share Your Story
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '1.1rem', md: '1.25rem' }, mb: 1 }}>
                Tell others what they should know (optional but helpful!)
              </Typography>
              <Chip label="This will be public" color="info" sx={{ fontWeight: 600 }} />
            </Box>
            <TextField
              placeholder="What would you tell a friend? Share tips, surprises, red flags, or anything that would help others decide. Be honest and specific! 💡"
              multiline
              minRows={6}
              maxRows={10}
              value={reviewForm.fullReview}
              onChange={(e) => setReviewForm((f) => ({ ...f, fullReview: e.target.value }))}
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  fontSize: '1.05rem',
                  backgroundColor: theme.palette.background.paper,
                  '& fieldset': {
                    borderWidth: 2,
                    borderColor: alpha(theme.palette.primary.main, 0.2),
                  },
                  '&:hover fieldset': {
                    borderColor: alpha(theme.palette.primary.main, 0.4),
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: theme.palette.primary.main,
                    borderWidth: 3,
                  },
                },
              }}
            />
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      fullScreen={fullScreen}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
          },
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, md: 4 },
          overflow: 'hidden',
          backgroundColor: theme.palette.background.default,
          minHeight: { xs: '100vh', md: '70vh' },
          boxShadow: 24,
        },
      }}
    >
      {/* Progress Bar */}
      <LinearProgress
        variant="determinate"
        value={progressPercent}
        sx={{
          height: 6,
          backgroundColor: alpha(theme.palette.primary.main, 0.1),
          '& .MuiLinearProgress-bar': {
            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            transition: 'transform 0.4s ease-in-out',
          },
        }}
      />

      {/* Header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.85)} 0%, ${alpha(theme.palette.primary.dark, 0.95)} 100%)`,
          color: theme.palette.common.white,
          px: { xs: 3, md: 5 },
          py: { xs: 2.5, md: 3 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: -50,
            right: -50,
            width: 200,
            height: 200,
            borderRadius: '50%',
            background: alpha(theme.palette.common.white, 0.06),
            filter: 'blur(60px)',
          }}
        />
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ position: 'relative', zIndex: 1 }}>
          {getDisplayStep() ? (
            <Typography variant="body2" fontWeight={600} sx={{ opacity: 0.9, fontSize: { xs: '0.85rem', sm: '1rem' } }}>
              Step {getDisplayStep()} of {totalCountedSteps}
            </Typography>
          ) : (
            <Box sx={{ width: 100 }} /> 
          )}
          <Stack direction="row" alignItems="center" spacing={2}>
            {currentStep > 0 && (
              <Typography variant="caption" sx={{ opacity: 0.8, fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
                {reviewCreator?.creatorName || reviewCreator?.username}
              </Typography>
            )}
            <IconButton
              onClick={handleClose}
              sx={{
                color: theme.palette.common.white,
                backgroundColor: alpha(theme.palette.common.white, 0.1),
                '&:hover': {
                  backgroundColor: alpha(theme.palette.common.white, 0.2),
                },
              }}
              size="small"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>
      </Box>

      {/* Content with Slide Animation */}
      <DialogContent
        sx={{
          px: { xs: 3, md: 5 },
          py: { xs: 3, md: 4 },
          backgroundColor: theme.palette.background.paper,
          minHeight: { xs: 'calc(100vh - 250px)', md: '400px' },
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Slide direction={slideDirection === 'left' ? 'left' : 'right'} in={true} timeout={300} key={currentStep}>
          <Box sx={{ width: '100%' }}>
            {renderStepContent()}
          </Box>
        </Slide>
      </DialogContent>

      {/* Navigation */}
      <DialogActions
        sx={{
          px: { xs: 3, md: 5 },
          py: { xs: 2.5, md: 3 },
          backgroundColor: alpha(theme.palette.background.default, 0.6),
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          gap: 2,
          justifyContent: 'space-between',
        }}
      >
        {/* Back Button */}
        {currentStep > 0 && (
          <Button
            onClick={handleBack}
            startIcon={<ArrowBackIcon />}
            sx={{
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: 2,
              px: { xs: 2, sm: 3 },
              py: 1.3,
            }}
          >
            Back
          </Button>
        )}
        
        {currentStep === 0 && <Box />}

        {/* Next/Submit Button */}
        {currentStep < totalSteps - 1 ? (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={!canProgress}
            endIcon={<ArrowForwardIcon />}
            sx={{
              px: { xs: 3, sm: 4 },
              py: 1.5,
              fontWeight: 700,
              fontSize: '1.05rem',
              textTransform: 'none',
              borderRadius: 2,
              background: !canProgress
                ? theme.palette.action.disabledBackground
                : `linear-gradient(120deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              boxShadow: !canProgress ? 'none' : '0 8px 24px rgba(30,136,229,0.35)',
              '&:hover': {
                background: !canProgress
                  ? theme.palette.action.disabledBackground
                  : `linear-gradient(120deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`,
                boxShadow: !canProgress ? 'none' : '0 12px 32px rgba(30,136,229,0.4)',
              },
            }}
          >
            {currentStep === 0 ? "Let's Begin" : 'Continue'}
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={onSubmit}
            disabled={isSubmitDisabled}
            startIcon={<CheckCircleIcon />}
            sx={{
              px: { xs: 4, sm: 5 },
              py: 1.5,
              fontWeight: 700,
              fontSize: '1.05rem',
              textTransform: 'none',
              borderRadius: 2,
              background: isSubmitDisabled
                ? theme.palette.action.disabledBackground
                : `linear-gradient(120deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
              boxShadow: isSubmitDisabled ? 'none' : '0 8px 24px rgba(76,175,80,0.35)',
              '&:hover': {
                background: isSubmitDisabled
                  ? theme.palette.action.disabledBackground
                  : `linear-gradient(120deg, ${theme.palette.success.dark}, ${theme.palette.success.dark})`,
                boxShadow: isSubmitDisabled ? 'none' : '0 12px 32px rgba(76,175,80,0.4)',
              },
            }}
          >
            Submit Review 🎉
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
